import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, ListChecks, FileSpreadsheet } from "lucide-react";
import { PageHeader } from "@/components/Layout";
import { StatusBadge, PriorityBadge, ApprovalBadge, ProgressBar, EmptyState } from "@/components/Bits";
import TaskFormDialog from "@/components/TaskFormDialog";
import TaskDrawer from "@/components/TaskDrawer";
import ImportListingsDialog from "@/components/ImportListingsDialog";
import { DIVISIONS, STATUSES } from "@/lib/ui";
import api from "@/lib/api";

const selCls =
  "bg-[#0a0a0a] border border-white/10 text-zinc-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-white transition-colors";

export default function Tasks() {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [clients, setClients] = useState([]);
  const [team, setTeam] = useState([]);
  const [division, setDivision] = useState("all");
  const [status, setStatus] = useState("all");
  const [formOpen, setFormOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [drawerId, setDrawerId] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const load = useCallback(() => {
    const params = {};
    if (division !== "all") params.division = division;
    if (status !== "all") params.status = status;
    api.get("/tasks", { params }).then((r) => setTasks(r.data));
  }, [division, status]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/clients").then((r) => setClients(r.data));
    api.get("/team").then((r) => setTeam(r.data));
  }, []);

  const openTask = (id) => navigate(`/tasks/${id}`);

  return (
    <div>
      <PageHeader
        title="All Agency Tasks"
        subtitle={`${tasks.length} tasks across all divisions`}
        actions={
          <>
            <button data-testid="import-listings-button" onClick={() => setImportOpen(true)} className="flex items-center gap-2 text-sm border border-white/15 text-zinc-200 hover:bg-white/5 px-4 py-2 rounded-sm transition-colors">
              <FileSpreadsheet size={16} /> Import Listings
            </button>
            <button data-testid="create-task-button" onClick={() => setFormOpen(true)} className="flex items-center gap-2 text-sm bg-white text-black font-medium px-4 py-2 rounded-sm hover:bg-zinc-200 transition-colors active:scale-[0.98]">
              <Plus size={16} /> Create Task
            </button>
          </>
        }
      />
      <div className="p-8 space-y-5">
        <div className="flex flex-wrap gap-3">
          <select data-testid="filter-division" className={selCls} value={division} onChange={(e) => setDivision(e.target.value)}>
            <option value="all">All Divisions</option>
            {DIVISIONS.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
          <select data-testid="filter-status" className={selCls} value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="all">All Status</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>

        {tasks.length === 0 ? (
          <EmptyState icon={ListChecks} title="No tasks found" hint="Adjust filters or create a new task." />
        ) : (
          <div className="bg-[#121212] border border-white/10 rounded-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left border-b border-white/10 bg-[#0a0a0a]">
                    {["ID", "Brand", "Division", "Deliverable", "Owner", "Priority", "Status", "Progress", "Approval"].map((h) => (
                      <th key={h} className="py-3 px-3 font-mono text-[11px] uppercase tracking-wider text-zinc-500">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {tasks.map((t) => (
                    <tr key={t.id} data-testid={`task-row-${t.id}`} onClick={() => openTask(t.id)} className="border-b border-white/5 hover:bg-white/5 cursor-pointer transition-colors">
                      <td className="py-3 px-3 font-mono text-xs text-zinc-500">{t.ref}</td>
                      <td className="py-3 px-3 text-zinc-200">{t.brand}</td>
                      <td className="py-3 px-3 text-zinc-400 text-xs">{t.division}</td>
                      <td className="py-3 px-3 text-zinc-300 max-w-[200px] truncate">{t.title}</td>
                      <td className="py-3 px-3 text-zinc-400">{t.assigned_to || "—"}</td>
                      <td className="py-3 px-3"><PriorityBadge priority={t.priority} /></td>
                      <td className="py-3 px-3"><StatusBadge status={t.status} /></td>
                      <td className="py-3 px-3 w-28">
                        <div className="flex items-center gap-2">
                          <ProgressBar value={t.progress} />
                          <span className="text-xs font-mono text-zinc-500 w-8">{t.progress}%</span>
                        </div>
                      </td>
                      <td className="py-3 px-3"><ApprovalBadge status={t.approval_status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <TaskFormDialog open={formOpen} onOpenChange={setFormOpen} clients={clients} team={team} onCreated={load} />
      <ImportListingsDialog open={importOpen} onOpenChange={setImportOpen} clients={clients} onImported={load} />
      <TaskDrawer taskId={drawerId} open={drawerOpen} onOpenChange={setDrawerOpen} onChanged={load} />
    </div>
  );
}
